"use client";

import { useState } from "react";
import { PillGroup, SelectField } from "@/components/tool-kit/primitives";
import { CellPanel } from "@/components/tools/chem-physical/cell-panel";
import { FreeEnergyPanel } from "@/components/tools/chem-physical/free-energy-panel";
import { NernstPanel } from "@/components/tools/chem-physical/nernst-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const NERNST_EQUATION_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeNernst: "Cell potential from Q (Nernst)",
  modeCell: "E° of a cell from half-cells",
  modeFreeEnergy: "ΔG = −nFE",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  solveLabel: "Solve for",
  results: "Results",
  working: "The working",
  answerSuffix: "{label} (answer)",
  tryLabel: "Try an example",
  exampleDaniell: "Daniell cell, Q = 10",
  exampleHydrogen: "Hydrogen electrode at pH 7",
  termE: "Cell potential (E)",
  termE0: "Standard potential (E°)",
  termQ: "Reaction quotient (Q)",
  termT: "Temperature (T)",
  termN: "Electrons transferred (n)",
  termDG: "Free energy change (ΔG)",
  unitVoltage: "Voltage unit",
  unitTemperature: "Temperature unit",
  unitFreeEnergy: "ΔG unit",
  solvedCell: "Solved below",
  qHint:
    "Dimensionless and positive: products over reactants, with solids and pure liquids left out. E-notation works: 1e14.",
  nHint: "A whole number: the electrons in the balanced equation.",
  deltaG: "ΔG = −nFE",
  cellState: "Cell",
  galvanic: "E > 0: spontaneous (galvanic)",
  nonSpontaneousCell: "E < 0: not spontaneous as written",
  cellDead: "E = 0: at equilibrium",
  slopeLabel: "Change in E per tenfold change in Q",
  log10K: "log₁₀ K",
  equation: "E = E° − (RT ÷ nF) ln Q",
  rearrangedE0: "E° = E + (RT ÷ nF) ln Q",
  rearrangedQ: "ln Q = (E° − E) × nF ÷ RT",
  rearrangedT: "T = (E° − E) × nF ÷ (R × ln Q)",
  convertLine: "{label}: {value} {unit} = {converted}",
  slopeLine:
    "RT ÷ nF = (8.314462618 J/(mol·K) × {t} K) ÷ ({n} × 96485.33212 C/mol) = {slope} V",
  lnQLine: "ln Q = ln({q}) = {lnq}",
  substitutedE: "E = {e0} − {slope} V × ({lnq}) = {result}",
  substitutedE0: "E° = {e} + {slope} V × ({lnq}) = {result}",
  substitutedQ: "ln Q = ({e0} − {e}) ÷ {slope} V = {lnq}, so Q = {result}",
  substitutedT:
    "T = ({e0} − {e}) × {n} × 96485.33212 ÷ (8.314462618 × ({lnq})) = {result}",
  deltaGLine: "ΔG = −nFE = −{n} × 96485.33212 C/mol × {e} V = {dg} J/mol",
  constantsNote:
    "R = 8.314462618 J/(mol·K) and F = 96485.33212 C/mol are exact (NIST, CODATA).",
  slopeNote:
    "Each tenfold change in Q shifts E by (RT ln 10) ÷ nF, which is 59.16 mV ÷ n at 25 °C. Q uses mol/L for solutes and bar for gases; pure solids and liquids are left out.",
  cathode: "Cathode (reduction)",
  anode: "Anode (oxidation)",
  cathodeHint: "The couple that gains electrons.",
  anodeHint: "The couple that loses electrons.",
  cellQ: "Reaction quotient Q (optional)",
  cellQHint:
    "Leave blank for standard conditions. Fill it in to get the Nernst potential as well.",
  cellE0: "Standard cell potential (E°cell)",
  cellENow: "Cell potential at this Q (E)",
  overallReaction: "Overall reaction",
  sameHalfCell: "Choose two different half-cells.",
  standardDeltaG: "Standard free energy change (ΔG°)",
  equilibriumK: "Equilibrium constant (K)",
  cathodeLine: "Cathode: {reaction}   E° = {e0}",
  anodeLine: "Anode (reversed): {reaction}   E° = {e0} as a reduction",
  multiplierLine:
    "Multiply the cathode reaction by {a} and the anode reaction by {b} so both transfer {n} electrons.",
  cellEquation: "E°cell = E°cathode − E°anode = {c} V − ({a} V) = {result}",
  tableSource:
    "Reduction potentials are from OpenStax Chemistry 2e, Appendix L, at 25 °C against the standard hydrogen electrode. Other tables differ in the third decimal for some couples.",
  feEquation: "ΔG = −nFE",
  feRearrangedE: "E = −ΔG ÷ (nF)",
  feRearrangedN: "n = −ΔG ÷ (F × E)",
  feSubstitutedG: "ΔG = −{n} × 96485.33212 C/mol × {e} V = {result}",
  feSubstitutedE: "E = −({g} J/mol) ÷ ({n} × 96485.33212 C/mol) = {result}",
  feSubstitutedN: "n = −({g} J/mol) ÷ (96485.33212 C/mol × {e} V) = {result}",
  fractionalN:
    "n comes out as {n}, which is not a whole number. Electrons transferred must be an integer, so check ΔG and E.",
  enterValues: "Enter the remaining values to see the answer.",
  errorUnreadable:
    "{label} is not a number. Use digits, such as 2.50 or 1.5e3.",
  errorNonPositive: "{label} must be greater than zero.",
  errorBelowZero:
    "{label} is at or below absolute zero (0 K, −273.15 °C, −459.67 °F), which is impossible.",
  errorBadN: "n must be a whole number of electrons, 1 or more.",
  errorZeroLogQ:
    "Q = 1, so ln Q = 0 and E equals E° at every temperature. Temperature cannot be found; use a different Q.",
  errorResultBelowZero:
    "No positive temperature fits these values. Check the signs of E° − E and ln Q.",
  errorSignMismatch:
    "For a positive number of electrons, ΔG and E must have opposite signs: a negative ΔG goes with a positive E.",
  errorZeroValue: "ΔG and E must not be zero when solving for n.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value}",
} as const;

type Mode = "nernst" | "cell" | "freeEnergy";
const SIG_CHOICES = ["3", "4", "5", "6"] as const;

export default function NernstEquationCalculatorWidget() {
  const strings = useToolUi(NERNST_EQUATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("nernst");
  const [sigChoice, setSigChoice] = useState("match");
  const panels = {
    nernst: <NernstPanel sigChoice={sigChoice} strings={strings} />,
    cell: <CellPanel sigChoice={sigChoice} strings={strings} />,
    freeEnergy: <FreeEnergyPanel sigChoice={sigChoice} strings={strings} />,
  };
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="nern-mode"
        onChange={setMode}
        options={[
          { value: "nernst", label: strings.modeNernst },
          { value: "cell", label: strings.modeCell },
          { value: "freeEnergy", label: strings.modeFreeEnergy },
        ]}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="nern-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      {panels[mode]}
    </>
  );
}
